import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'

import { RecordLink } from '../../components/RecordLink'
import { RecordPicker } from '../../components/RecordPicker'
import { describeBookRecord, type BookRecord } from '../../components/bookRecord'
import { apiErrorMessage } from '../../lib/errors'
import { searchActiveBrokers } from '../brokers/api'
import type { DatBrokerOption, DatBrokerResolution } from './api'
import type { ChosenBroker, PasteCompany } from './pasteCompanies'

/**
 * Who the broker is behind each company the selected rows name (the capture from the
 * paste, decisions 2 and 4). The book's answer is said in words — which contact, whose
 * domain, which name — and the broker chosen sits beside it in a picker that offers the
 * answer's brokers first and searches the rest of the book, because the dispatcher knows
 * things the book does not.
 *
 * <p>
 * A company the book knows by a contact is chosen already; a candidate or a suggestion is
 * one click away. With no broker chosen, a company's loads are filed under the name DAT
 * printed: Hugo registers a broker when he books one of its loads, never to capture one
 * (TRKC-104). Registering one the book does not know stays on offer, in a new tab, and the
 * screen asks again when the dispatcher comes back to it.
 * </p>
 */
export function DatBrokersPanel({
  companies,
  resolutions,
  brokerOf,
  onChoose,
  isChecking,
  failure,
  onCheckAgain,
  disabled,
}: {
  companies: PasteCompany[]
  resolutions: Readonly<Record<string, DatBrokerResolution>>
  brokerOf: (key: string) => ChosenBroker | null
  onChoose: (key: string, broker: ChosenBroker | null) => void
  isChecking: boolean
  failure: { error: unknown } | null
  onCheckAgain: () => void
  disabled: boolean
}) {
  const { t } = useTranslation()

  return (
    <section className="loads__brokers" aria-labelledby="paste-brokers-heading">
      <h2 id="paste-brokers-heading">{t('loads.paste.brokers.heading')}</h2>
      <p className="field__hint">{t('loads.paste.brokers.hint')}</p>

      {failure !== null && (
        <p className="alert" role="alert">
          {apiErrorMessage(failure.error, t, 'loads.paste.brokers.failed')}
        </p>
      )}

      <ul className="loads__companies">
        {companies.map((company, index) => (
          <CompanyLine
            key={company.key}
            index={index}
            company={company}
            resolution={resolutions[company.key]}
            chosen={brokerOf(company.key)}
            onChoose={(broker) => onChoose(company.key, broker)}
            isChecking={isChecking}
            disabled={disabled}
          />
        ))}
      </ul>

      <button className="button button--quiet" type="button" onClick={onCheckAgain} disabled={disabled || isChecking}>
        {isChecking ? t('loads.paste.brokers.checking') : t('loads.paste.brokers.checkAgain')}
      </button>
    </section>
  )
}

function CompanyLine({
  index,
  company,
  resolution,
  chosen,
  onChoose,
  isChecking,
  disabled,
}: {
  index: number
  company: PasteCompany
  resolution: DatBrokerResolution | undefined
  chosen: ChosenBroker | null
  onChoose: (broker: ChosenBroker | null) => void
  isChecking: boolean
  disabled: boolean
}) {
  const { t } = useTranslation()
  const offered = resolution?.status === 'Unknown' ? [] : (resolution?.brokers ?? [])

  // The brokers the answer offered first, then any other broker of the book, searched: the
  // one a person knows it is, whatever the book could tell from DAT's row.
  const suggested: BookRecord[] = offered.map((option) => ({ id: option.brokerId, name: option.name }))

  const single =
    (resolution?.status === 'Candidate' || resolution?.status === 'Suggested') && offered.length === 1 ? offered[0]! : null

  // Registering is offered only when nobody in the book — not even a broker we stopped
  // working with — answers to the company. Reactivating that one is the way back: the draft
  // carries no MC, so nothing would refuse the same company registered twice (GPT-5.6 Sol on #40).
  const offerToRegister = resolution?.status === 'Unknown' && resolution.stoodDown.length === 0

  return (
    <li className="loads__company">
      <p>
        <strong>{company.company}</strong>{' '}
        <span className="muted">· {t('loads.paste.brokers.loads', { count: company.rows.length })}</span>
      </p>

      <p className="field__hint">
        {resolution === undefined
          ? isChecking
            ? t('loads.paste.brokers.checking')
            : t('loads.paste.brokers.notAsked')
          : sentence(resolution, t)}
      </p>

      {resolution?.stoodDown.map((broker) => (
        <p key={broker.brokerId} className="field__hint">
          {t('loads.paste.brokers.stoodDown', { name: broker.name })}{' '}
          <RecordLink subject={{ kind: 'Broker', id: broker.brokerId, name: broker.name }} />
        </p>
      ))}

      <div className="loads__company-actions">
        <RecordPicker<BookRecord>
          name={`paste-broker-${index}`}
          label={t('loads.paste.brokers.select', { company: company.company })}
          value={chosen}
          onChange={(broker) => onChoose(broker === null ? null : { id: broker.id, name: broker.name })}
          search={searchActiveBrokers}
          suggested={suggested}
          describe={(record) => describeBookRecord(record, t)}
          noneLabel={t('loads.paste.brokers.none')}
          placeholder={t('picker.placeholder')}
          disabled={disabled}
        />

        {single !== null && chosen?.id !== single.brokerId && (
          <button
            className="button button--quiet"
            type="button"
            disabled={disabled}
            onClick={() => onChoose({ id: single.brokerId, name: single.name })}
          >
            {t('loads.paste.brokers.use', { name: single.name })}
          </button>
        )}

        {offerToRegister && (
          <a href={registerLink(company)} target="_blank" rel="noopener noreferrer">
            {t('loads.paste.brokers.register')}
          </a>
        )}
      </div>

      {offerToRegister && <p className="field__hint">{t('loads.paste.brokers.registerHint')}</p>}
    </li>
  )
}

/** The evidence that names a broker by one of the company's own contacts, not by a domain or a name. */
const BY_CONTACT: ReadonlySet<string> = new Set(['Agent', 'Broker', 'Learned'])

/** What the book said about a company, in the dispatcher's language. */
function sentence(resolution: DatBrokerResolution, t: TFunction): string {
  const first: DatBrokerOption | undefined = resolution.brokers[0]
  const count = resolution.brokers.length

  switch (resolution.status) {
    case 'Known':
      return first === undefined
        ? t('loads.paste.brokers.unknown')
        : first.foundBy === 'Agent'
          ? t('loads.paste.brokers.knownAgent', { evidence: first.evidence, agent: first.agentName, broker: first.name })
          : first.foundBy === 'Learned'
            ? t('loads.paste.brokers.knownLearned', { evidence: first.evidence, broker: first.name })
            : t('loads.paste.brokers.knownBroker', { evidence: first.evidence, broker: first.name })
    case 'Ambiguous':
      return t('loads.paste.brokers.ambiguous', { evidence: first?.evidence })
    case 'Candidate':
      return t('loads.paste.brokers.candidate', { count, broker: first?.name, evidence: first?.evidence })
    case 'Suggested':
      return t('loads.paste.brokers.suggested', { count, broker: first?.name })
    default:
      // A contact that is a stood-down broker's makes the capture refuse the company's rows
      // (TRKC-104): saying they are filed under its name would promise what will not happen.
      return resolution.stoodDown.some((option) => BY_CONTACT.has(option.foundBy))
        ? t('loads.paste.brokers.heldByStoodDown')
        : t('loads.paste.brokers.unknown')
  }
}

/**
 * The broker form, filled with what DAT printed for the company. It opens in a new tab so
 * this screen keeps its rows, ticks and corrections; coming back asks the book again.
 *
 * <p>
 * In the fragment, never the query: a fragment does not leave the browser, so a rep's
 * address and phone never land in the access log of the server that serves the page.
 * </p>
 */
function registerLink(company: Pick<PasteCompany, 'company' | 'contacts'>): string {
  const draft = new URLSearchParams({ name: company.company })
  const email = company.contacts.find((contact) => contact.email !== null)?.email
  const phone = company.contacts.find((contact) => contact.phone !== null)?.phone

  if (email != null) {
    draft.set('email', email)
  }

  if (phone != null) {
    draft.set('phone', phone)
  }

  return `/brokers/new#${draft.toString()}`
}
